import type { ItpDataGridAggregate, ItpDataGridColumn } from './types';
import { columnKey, columnValueReader } from './gridCell';

export function isSubtotalColumn(column: ItpDataGridColumn): boolean {
  return column.subtotal ?? column.type === 'number';
}

export function aggregateOf(column: ItpDataGridColumn): ItpDataGridAggregate {
  return column.aggregate ?? 'sum';
}

function decimalPlaces(value: number): number {
  const [digits, exponent] = String(value).split('e');
  const dot = digits.indexOf('.');
  const places = dot === -1 ? 0 : digits.length - dot - 1;
  return Math.max(0, places - Number(exponent ?? 0));
}

export function aggregateColumn(
  rows: readonly any[],
  column: ItpDataGridColumn,
): number {
  const read = columnValueReader(column);
  const kind = aggregateOf(column);

  if (kind === 'count') {
    let count = 0;
    for (const row of rows) {
      const value = read(row);
      if (value != null && value !== '') {
        count += 1;
      }
    }
    return count;
  }

  let sum = 0;
  let places = 0;
  let seen = 0;
  let min = Infinity;
  let max = -Infinity;

  for (const row of rows) {
    const raw = read(row);
    if (raw == null || raw === '') {
      continue;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      continue;
    }
    seen += 1;
    sum += value;
    places = Math.max(places, decimalPlaces(value));
    if (value < min) {
      min = value;
    }
    if (value > max) {
      max = value;
    }
  }

  if (seen === 0) {
    return 0;
  }

  switch (kind) {
    case 'avg':
      return sum / seen;
    case 'min':
      return min;
    case 'max':
      return max;
    default:
      return Number(sum.toFixed(Math.min(places, 100)));
  }
}

export function aggregateRows(
  rows: readonly any[],
  columns: readonly ItpDataGridColumn[],
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const column of columns) {
    totals[columnKey(column)] = aggregateColumn(rows, column);
  }
  return totals;
}
